import React, { useState, useEffect } from 'react';
import { LanguageProvider, useLanguage } from './components/LanguageContext';
import Login from './components/Login';
import Signup from './components/Signup';
import VerifyEmail from './components/VerifyEmail';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import Dashboard from './components/Dashboard';
import Branches from './components/Branches';
import Users from './components/Users';
import Admins from './components/Admins';
import Reports from './components/Reports';
import Products from './components/Products';
import Medications from './components/Medications';
import { getAdminUser, getAdminToken, clearAdminAuth, apiGet } from './utils/api';
import { visibleNavItems, NAV_ITEMS } from './navItems';
import { LogoutIcon } from './components/NavIcons';
import SidebarBrand from './components/SidebarBrand';
import SidebarUser from './components/SidebarUser';
import useIsMobile from './hooks/useIsMobile';
import MobileHeader from './components/MobileHeader';
import NavDrawer from './components/NavDrawer';
import BottomNav from './components/BottomNav';

// Parse initial page from URL
const getInitialPage = () => {
  const path = window.location.pathname;
  if (path.includes('verify-email')) return 'verify-email';
  if (path.includes('reset-password')) return 'reset-password';
  if (path.includes('signup')) return 'signup';
  if (path.includes('forgot-password')) return 'forgot-password';
  return 'login';
};

const AppContent = () => {
  const { print_lang } = useLanguage();
  const [admin, setAdmin] = useState(null);
  const [currentPage, setCurrentPage] = useState(getInitialPage);
  const [loading, setLoading] = useState(true);
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Check auth on mount (skip for public pages)
  useEffect(() => {
    const checkAuth = async () => {
      // Don't redirect away from verification/reset pages
      const publicPages = ['verify-email', 'reset-password', 'signup', 'forgot-password'];
      const initialPage = getInitialPage();

      if (publicPages.includes(initialPage)) {
        setLoading(false);
        return;
      }

      const token = getAdminToken();
      const storedAdmin = getAdminUser();

      if (token && storedAdmin) {
        // Outside the `if (data.success)` below on purpose: currentPage starts
        // at 'login' for anyone who did not land on a public page, and a
        // restored session renders the authenticated layout, where 'login'
        // matches no <main> branch — correct sidebar beside a blank page. It
        // has to be set for the restore, not only for a fresh /me success.
        setCurrentPage('dashboard');
        try {
          const data = await apiGet('/api/admin/me');
          if (data.success) {
            setAdmin(data.data);
          } else {
            clearAdminAuth();
            setAdmin(null);
          }
        } catch (err) {
          clearAdminAuth();
          setAdmin(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const handleLogin = (adminData) => {
    setAdmin(adminData);
    setCurrentPage('dashboard');
    window.history.pushState({}, '', '/dashboard');
  };

  const handleLogout = () => {
    clearAdminAuth();
    setAdmin(null);
    setCurrentPage('login');
    window.history.pushState({}, '', '/login');
  };

  const handleNavigate = (page) => {
    setCurrentPage(page);
    window.history.pushState({}, '', `/${page}`);
  };

  // Get token from URL for verification/reset pages
  const getTokenFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('token');
  };

  if (loading) {
    return (
      <div className="loading-container" style={{ height: '100vh' }}>
        <div className="spinner" />
      </div>
    );
  }

  // Unauthenticated routes
  if (!admin) {
    switch (currentPage) {
      case 'signup':
        return <Signup onNavigate={handleNavigate} />;
      case 'verify-email':
        return <VerifyEmail token={getTokenFromUrl()} onNavigate={handleNavigate} />;
      case 'forgot-password':
        return <ForgotPassword onNavigate={handleNavigate} />;
      case 'reset-password':
        return <ResetPassword token={getTokenFromUrl()} onNavigate={handleNavigate} />;
      default:
        return <Login onLogin={handleLogin} onNavigate={handleNavigate} />;
    }
  }

  const currentNavItem = NAV_ITEMS.find((item) => item.id === currentPage);
  const pageTitle = currentNavItem
    ? print_lang(currentNavItem.labelKey, currentNavItem.labelFallback)
    : print_lang('nav.dashboard', 'Dashboard');

  // Authenticated layout
  return (
    <div className="app-container">
      {!isMobile && (
        <aside className="sidebar">
          <div className="sidebar-header">
            <SidebarBrand />
          </div>

          <nav>
            <ul className="sidebar-nav">
              {visibleNavItems(admin).map(({ id, labelKey, labelFallback, Icon }) => (
                <li
                  key={id}
                  className={`sidebar-nav-item ${currentPage === id ? 'active' : ''}`}
                  onClick={() => handleNavigate(id)}
                >
                  <Icon className="sidebar-nav-icon" />
                  {print_lang(labelKey, labelFallback)}
                </li>
              ))}

              <li className="sidebar-nav-item" onClick={handleLogout}>
                <LogoutIcon className="sidebar-nav-icon" />
                {print_lang('nav.logout', 'Logout')}
              </li>
            </ul>
          </nav>

          <div className="sidebar-footer">
            <SidebarUser admin={admin} userClassName="sidebar-user" />
          </div>
        </aside>
      )}

      {isMobile && (
        <>
          <MobileHeader
            title={pageTitle}
            onOpenDrawer={() => setDrawerOpen(true)}
            admin={admin}
          />
          <NavDrawer
            open={drawerOpen}
            admin={admin}
            currentPage={currentPage}
            onNavigate={handleNavigate}
            onLogout={handleLogout}
            onClose={() => setDrawerOpen(false)}
          />
        </>
      )}

      {/* Main Content */}
      <main className="main-content">
        {currentPage === 'dashboard' && <Dashboard admin={admin} onNavigate={handleNavigate} />}
        {currentPage === 'branches' && <Branches admin={admin} />}
        {currentPage === 'users' && <Users admin={admin} />}
        {currentPage === 'reports' && <Reports />}
        {currentPage === 'products' && <Products />}
        {currentPage === 'medications' && <Medications />}
        {currentPage === 'admins' && admin.is_super_admin && <Admins />}
      </main>

      {isMobile && (
        <BottomNav
          admin={admin}
          currentPage={currentPage}
          onNavigate={handleNavigate}
          onLogout={handleLogout}
        />
      )}
    </div>
  );
};

const App = () => {
  return (
    <LanguageProvider>
      <AppContent />
    </LanguageProvider>
  );
};

export default App;
